/**
 * The runtime breaker (ADR 0089): one per (machine, agent kind, setup). A runtime fault
 * (runtime-faults.ts) opens it; Orgus then starts nothing new on that pair. It never closes on a
 * timer: a self-test on the machine (the agent run in a throwaway repo through the launch path
 * missions use) moves it to half-open, the next session there runs alone, and the first that ends
 * without an agent failure closes it. A setup change (a new agent version, a new Orgus agent, a
 * new environment fingerprint) sends it straight to a self-test.
 *
 *   open ──self-test due──▶ testing ──pass──▶ half-open ──one good session──▶ (closed: no record)
 *     ▲                        │fail                │runtime fault or agent failure
 *     └────────────────────────┴────────────────────┘
 *
 * Pure and browser-safe: the hub's scheduler keeps the records (its store), placement reads
 * `brokenKindsOf`, the console shows them.
 */
import type { Id, Ts } from '../index.ts';
import { agentKindLabel } from './adapters.ts';
import type { RuntimeFault, RuntimeSignatureId } from './runtime-faults.ts';

/** The module message types (agent module 'agent-health', hub module 'scheduler'). */
export const AGENT_HEALTH_EXT = 'agent-health';
/** hub to agent: run one self-test (SelfTestRequest). */
export const SELF_TEST_RUN_TYPE = 'selftest.run';
/** agent to hub: a self-test's result (SelfTestResult). */
export const SELF_TEST_RESULT_TYPE = 'selftest.result';
/** agent to hub: this machine's setup (MachineSetupReport), on connect and when it changes. */
export const MACHINE_SETUP_TYPE = 'setup';
/** agent to hub: the agent's own event loop froze (LoopFreezeReport): the machine's fault, not an agent's. */
export const LOOP_FROZEN_TYPE = 'loop.frozen';

/** After a breaker opens, the first self-test waits this long (a fix in flight, a reboot). */
export const SELF_TEST_BACKOFF_MS = 2 * 60_000;
/** At most one self-test per pair this often. */
export const SELF_TEST_EVERY_MS = 15 * 60_000;
/** ... and this often for an agent whose credits the owner saves (agents.spare). */
export const SELF_TEST_SPARE_EVERY_MS = 60 * 60_000;
/** A self-test that runs longer than this is stopped and fails at its step 'timeout'. */
export const SELF_TEST_TIMEOUT_MS = 5 * 60_000;
/** Its cost cap: the run is stopped past this many tokens (input plus output). */
export const SELF_TEST_TOKEN_CAP = 300_000;
/** Still open after this long: one escalation, then nothing more. */
export const BREAKER_ESCALATE_MS = 12 * 3_600_000;
/** A testing breaker whose result never came back opens again after this. */
export const SELF_TEST_LOST_MS = SELF_TEST_TIMEOUT_MS + 5 * 60_000;

/** What the agent runs with: a change sends an open breaker to a self-test. */
export interface AgentSetup {
  /** The agent CLI's version (Machine.runtimes), null when unknown. */
  agentVersion: string | null;
  /** The Orgus agent's version (Machine.agentVersion). */
  orgusVersion: string | null;
  /**
   * The launch environment's fingerprint: what PATH is and where the core tools resolve
   * (MachineSetupReport.fingerprint). ADR 0088's parity report supersedes it when it lands.
   */
  envFingerprint: string | null;
}

export function setupKeyOf(s: AgentSetup): string {
  return `${s.agentVersion ?? '?'}|${s.orgusVersion ?? '?'}|${s.envFingerprint ?? '?'}`;
}

export type SelfTestStep = 'start' | 'shell' | 'git-status' | 'commit' | 'merge' | 'package-manager' | 'runtime' | 'timeout' | 'cost';

export const SELF_TEST_STEPS: readonly SelfTestStep[] = ['start', 'shell', 'git-status', 'commit', 'merge', 'package-manager'];

export const SELF_TEST_STEP_LABEL: Record<SelfTestStep, string> = {
  start: 'Start the agent',
  shell: 'Run a shell command',
  'git-status': 'Run git status',
  commit: 'Create and commit a file',
  merge: 'Merge a prepared branch',
  'package-manager': 'Run the package manager',
  runtime: 'No runtime fault',
  timeout: 'Finish in time',
  cost: 'Stay under the cost cap',
};

/** hub to agent: SELF_TEST_RUN_TYPE. */
export interface SelfTestRequest {
  requestId: Id;
  kind: string;
  timeoutMs: number;
  tokenCap: number;
  /** The run mode the missions use (a failure storm's breaker tests in the mode that stormed). */
  permissionMode?: string | null;
}

export interface SelfTestStepResult {
  step: SelfTestStep;
  ok: boolean;
  /** What Orgus read: the repo state or the command's own output, clipped. Never the agent's word. */
  evidence: string;
}

/** agent to hub: SELF_TEST_RESULT_TYPE. */
export interface SelfTestResult {
  requestId: Id;
  machineId: Id;
  kind: string;
  passed: boolean;
  /** The first step that failed, null when it passed. */
  failedStep: SelfTestStep | null;
  steps: SelfTestStepResult[];
  /** A runtime fault the run showed, with its evidence. */
  fault?: RuntimeFault | null;
  sessionId?: Id | null;
  /** The setup it ran with (the agent's own view; the hub keys the breaker on its own). */
  envFingerprint?: string | null;
  tokens?: number;
  durationMs: number;
  /** The machine cannot run a self-test (the Go agent, ADR 0087 decision 3): it skipped it. */
  skipped?: boolean;
  at: Ts;
}

/** agent to hub: MACHINE_SETUP_TYPE. */
export interface MachineSetupReport {
  fingerprint: string;
  at: Ts;
}

/** agent to hub: LOOP_FROZEN_TYPE. */
export interface LoopFreezeReport {
  /** The longest freeze seen, ms. */
  maxMs: number;
  /** Freezes past the threshold since the last report. */
  count: number;
  at: Ts;
}

/** A remedy Orgus applied for a signature (BREAKER_PLAYBOOK). */
export interface BreakerRemedy {
  id: BreakerRemedyId;
  at: Ts;
  /** One plain sentence: what Orgus did. */
  did: string;
}

export type BreakerState = 'open' | 'testing' | 'half-open';

export interface AgentBreaker {
  machineId: Id;
  kind: string;
  setup: AgentSetup;
  state: BreakerState;
  /** The fault that opened it, kept as the evidence. */
  fault: RuntimeFault;
  /** The session that showed it. */
  sessionId: Id | null;
  /** The agent its work moved to, when it moved. */
  movedTo?: string | null;
  openedAt: Ts;
  /** A self-test may run from then (backoff, the interval, or at once after a setup change). */
  testAfter: Ts;
  /** The self-test in flight (state 'testing'). */
  testing?: { requestId: Id; at: Ts } | null;
  lastTest?: SelfTestResult | null;
  /** Half-open: the queue entry (or session) running alone on it. */
  trial?: Id | null;
  remedy?: BreakerRemedy | null;
  notifiedAt?: Ts | null;
  escalatedAt?: Ts | null;
  /** Times it opened again (a failed self-test, a failed trial). */
  reopened?: number;
  /** A setup change or a remedy: the next self-test runs once testAfter passes, whatever the interval. */
  retestNow?: boolean;
  /** Opened by a failure storm (ORGU-194), not a runtime fault: it is on whatever the runtimeBreaker setting says. */
  storm?: FailureStorm | null;
  at: Ts;
}

/**
 * Failure storms (ORGU-194, R-PL-6): STORM_FAILURES environment or launch failures on one
 * machine, agent kind and run mode within STORM_WINDOW_MS open the pair's breaker: Autopilot
 * starts nothing new there and one incident is raised. Only a passing self-test (in the storm's
 * run mode) closes it. A machine that cannot run the test skips it and the kind goes straight to
 * exploration: one trial, as after a pass.
 */
export const STORM_FAILURES = 3;
export const STORM_WINDOW_MS = 15 * 60_000;
/** Self-test budget for a storm: create, commit and merge main in a throwaway repo within 3 minutes. */
export const STORM_SELF_TEST_TIMEOUT_MS = 3 * 60_000;

export type StormCause = 'environment' | 'launch';

export function isStormCause(cause: string | null | undefined): cause is StormCause {
  return cause === 'environment' || cause === 'launch';
}

export interface FailureStorm {
  cause: StormCause;
  /** Failures in the window when it opened. */
  count: number;
  /** The run mode they failed in (Session.permissionMode), null for the agent's default. */
  runMode: string | null;
  /** The last failure's summary, clipped. */
  summary: string;
}

/** The failure times of one (machine, kind, run mode) inside the window, `at` added. */
export function stormWindow(recent: readonly Ts[], at: Ts): Ts[] {
  return [...recent.filter((t) => at - t < STORM_WINDOW_MS && t <= at), at].slice(-STORM_FAILURES * 4);
}

export function isStorm(recent: readonly Ts[]): boolean {
  return recent.length >= STORM_FAILURES;
}

/** The breaker a storm opens (an existing breaker on the pair keeps its own state). */
export function openStormBreaker(prev: AgentBreaker | null | undefined, f: { machineId: Id; kind: string; setup: AgentSetup; storm: FailureStorm; at: Ts }, now: Ts): AgentBreaker {
  if (prev && prev.state !== 'half-open') return prev;
  const fault: RuntimeFault = { signature: 'failure-storm', label: 'Its environment or launch failed again and again', key: `failure-storm:${f.storm.cause}`, count: f.storm.count, excerpt: f.storm.summary, command: null, source: 'tool', firstAt: f.at, at: f.at };
  return { ...openBreaker(prev, { machineId: f.machineId, kind: f.kind, setup: f.setup, fault, sessionId: null }, now), storm: f.storm };
}

/** A storm's breaker closes at the pass itself (ORGU-194 AC-2); a runtime fault's goes half-open first. */
export function closesOnPass(b: Pick<AgentBreaker, 'storm'>, r: Pick<SelfTestResult, 'passed' | 'skipped'>): boolean {
  return !!b.storm && r.passed && !r.skipped;
}

/** The machine skipped the self-test (ADR 0087): exploration, one trial decides. */
export function selfTestSkipped(b: AgentBreaker, now: Ts): AgentBreaker {
  return { ...b, state: 'half-open', testing: null, trial: null, at: now };
}

export type BreakerRemedyId = 'parity-recheck';

/**
 * Known fixes (ADR 0089 decision 4): a signature Orgus can address itself. A missing tool or a
 * file Windows has no app for (an extensionless shim, the "Pick an app" dialog) is the launch
 * environment's PATH: the environment module re-checks parity and fixes it, then the self-test
 * runs again. The rest has no automatic remedy: Orgus tells the person once.
 */
export const BREAKER_PLAYBOOK: Partial<Record<RuntimeSignatureId, { remedy: BreakerRemedyId; did: string; recommend: string }>> = {
  'no-file-association': {
    remedy: 'parity-recheck',
    did: 'checked the launch environment against yours and asked for a PATH fix',
    recommend: 'Make sure each tool on PATH resolves to a .cmd or .exe, not an extensionless shim.',
  },
  'tool-missing': {
    remedy: 'parity-recheck',
    did: 'checked the launch environment against yours and asked for a PATH fix',
    recommend: 'Install the tool, or put it on the PATH Orgus launches agents with.',
  },
  'not-recognized': {
    remedy: 'parity-recheck',
    did: 'checked the launch environment against yours and asked for a PATH fix',
    recommend: 'Install the command, or put it on the PATH Orgus launches agents with.',
  },
};

/** What Orgus recommends for a signature it cannot fix itself. */
export const BREAKER_RECOMMEND: Record<RuntimeSignatureId, string> = {
  'attach-console': 'Update the agent CLI, or turn off its interactive shell, then run Test now.',
  'node-pty': 'Update the agent CLI, or turn off its interactive shell, then run Test now.',
  'git-metadata-denied': "Let the agent's sandbox write the repository's .git folder (or run it in Accept edits), then run Test now.",
  'sandbox-denied': "Widen the agent's sandbox for the work folder, then run Test now.",
  'tool-missing': BREAKER_PLAYBOOK['tool-missing']!.recommend,
  'not-recognized': BREAKER_PLAYBOOK['not-recognized']!.recommend,
  'shell-missing': 'Install the shell the agent uses, or fix the path it is configured with, then run Test now.',
  'no-file-association': BREAKER_PLAYBOOK['no-file-association']!.recommend,
  'access-denied': 'Give this user write access to the folders the agent works in, then run Test now.',
  'failure-storm': "Open the failed missions' reports, fix what their environment lacks, then run Test now.",
};

/** A runtime fault on a pair: a new open breaker, or the one already there (an open one keeps its evidence). */
export function openBreaker(prev: AgentBreaker | null | undefined, f: { machineId: Id; kind: string; setup: AgentSetup; fault: RuntimeFault; sessionId: Id | null }, now: Ts): AgentBreaker {
  if (prev && prev.state !== 'half-open') return prev.state === 'testing' ? prev : { ...prev, at: now };
  return {
    machineId: f.machineId,
    kind: f.kind,
    setup: f.setup,
    state: 'open',
    fault: f.fault,
    sessionId: f.sessionId,
    openedAt: prev?.openedAt ?? now,
    testAfter: now + SELF_TEST_BACKOFF_MS,
    testing: null,
    lastTest: prev?.lastTest ?? null,
    trial: null,
    remedy: prev?.remedy ?? null,
    notifiedAt: prev?.notifiedAt ?? null,
    escalatedAt: prev?.escalatedAt ?? null,
    reopened: prev ? (prev.reopened ?? 0) + 1 : 0,
    at: now,
  };
}

/** Does the breaker stop new work on its pair? Open and testing do; half-open lets one trial through. */
export function breakerBlocks(b: Pick<AgentBreaker, 'state' | 'trial'>): boolean {
  return b.state !== 'half-open' || !!b.trial;
}

/** Kinds whose breaker stops new work, by machine: machine id to kind to the reason (one clause). */
export type BrokenMap = Readonly<Record<Id, Readonly<Record<string, string>>>>;

export function brokenKindsOf(breakers: Iterable<AgentBreaker>): Record<Id, Record<string, string>> {
  const out: Record<Id, Record<string, string>> = {};
  for (const b of breakers) if (breakerBlocks(b)) (out[b.machineId] ??= {})[b.kind] = breakerReason(b);
  return out;
}

/** "git could not write its own files", "a self-test runs now", "one mission runs first". */
export function breakerReason(b: Pick<AgentBreaker, 'state' | 'fault' | 'trial'>): string {
  if (b.state === 'testing') return 'a self-test runs now';
  if (b.state === 'half-open') return 'one mission runs first after its self-test passed';
  return b.fault.label.charAt(0).toLowerCase() + b.fault.label.slice(1);
}

/** How the console names a pair: Broken, Testing or Limited (half-open). */
export const BREAKER_STATE_LABEL: Record<BreakerState, string> = { open: 'Broken', testing: 'Testing', 'half-open': 'Limited' };

/**
 * Is a self-test due on this open breaker now? Never for an agent that is off or that the
 * machine's ceiling refuses; at most every SELF_TEST_EVERY_MS (SELF_TEST_SPARE_EVERY_MS when
 * its credits are saved). `force`: a person asked (Test now), which skips the wait, not the rules.
 */
export function selfTestDue(b: AgentBreaker, now: Ts, o: { off?: boolean; refused?: boolean; spare?: boolean; online?: boolean; force?: boolean } = {}): boolean {
  if (b.state !== 'open' || o.off || o.refused || o.online === false) return false;
  if (o.force) return true;
  if (now < b.testAfter) return false;
  if (b.retestNow || !b.lastTest) return true;
  return now - b.lastTest.at >= (o.spare ? SELF_TEST_SPARE_EVERY_MS : SELF_TEST_EVERY_MS);
}

/** The breaker once a self-test was asked for. */
export function startSelfTest(b: AgentBreaker, requestId: Id, now: Ts): AgentBreaker {
  return { ...b, state: 'testing', testing: { requestId, at: now }, retestNow: false, at: now };
}

/** A remedy was applied: test again at once (after the backoff a fix needs). */
export function withRemedy(b: AgentBreaker, remedy: BreakerRemedy, now: Ts): AgentBreaker {
  return { ...b, remedy, retestNow: true, testAfter: Math.min(b.testAfter, now + SELF_TEST_BACKOFF_MS), at: now };
}

/** A self-test came back: a pass makes it half-open, a failure opens it again until the next one is due. */
export function afterSelfTest(b: AgentBreaker, r: SelfTestResult, now: Ts): AgentBreaker {
  if (r.skipped) return selfTestSkipped({ ...b, lastTest: r }, now);
  if (r.passed) return { ...b, state: 'half-open', testing: null, lastTest: r, trial: null, at: now };
  return { ...b, state: 'open', testing: null, lastTest: r, testAfter: now + SELF_TEST_EVERY_MS, reopened: (b.reopened ?? 0) + 1, ...(r.fault ? { fault: r.fault } : {}), at: now };
}

/** A self-test that never came back (the machine went away): open again, due at once. */
export function selfTestLost(b: AgentBreaker, now: Ts): AgentBreaker | null {
  if (b.state !== 'testing' || !b.testing || now - b.testing.at < SELF_TEST_LOST_MS) return null;
  return { ...b, state: 'open', testing: null, testAfter: now, at: now };
}

/** The setup changed (a new version, a new environment): an open breaker tests at once, keyed on the new setup. */
export function setupChanged(b: AgentBreaker, setup: AgentSetup, now: Ts): AgentBreaker | null {
  if (setupKeyOf(setup) === setupKeyOf(b.setup)) return null;
  return { ...b, setup, ...(b.state === 'open' ? { testAfter: now, retestNow: true } : {}), at: now };
}

/** A half-open breaker's trial ended: fine closes it (null), an agent failure opens it again. */
export function afterTrial(b: AgentBreaker, ok: boolean, now: Ts): AgentBreaker | null {
  if (b.state !== 'half-open') return b;
  if (ok) return null;
  return { ...b, state: 'open', trial: null, testAfter: now + SELF_TEST_EVERY_MS, reopened: (b.reopened ?? 0) + 1, at: now };
}

/** Should Orgus tell the person now? Once per open breaker (after a remedy had its chance), and one escalation. */
export function breakerNotice(b: AgentBreaker, now: Ts): 'notify' | 'escalate' | null {
  if (b.state === 'half-open') return null;
  if (!b.notifiedAt) {
    // A known fix runs first; the person hears only when its self-test failed too.
    const fix = BREAKER_PLAYBOOK[b.fault.signature];
    if (fix && (!b.remedy || !b.lastTest || b.lastTest.at < b.remedy.at)) return null;
    return 'notify';
  }
  if (!b.escalatedAt && now - b.openedAt >= BREAKER_ESCALATE_MS) return 'escalate';
  return null;
}

/** "Codex is broken on Studio: git could not write its own files." */
export function breakerTitle(b: Pick<AgentBreaker, 'kind' | 'fault'>, machineName: string): string {
  return `${agentKindLabel(b.kind)} is broken on ${machineName}: ${b.fault.label.charAt(0).toLowerCase()}${b.fault.label.slice(1)}`;
}

/** What failed, the evidence, what Orgus did, what it recommends: the notification and incident body. */
export function breakerBody(b: AgentBreaker, machineName: string): string {
  const label = agentKindLabel(b.kind);
  if (b.storm) {
    const mode = b.storm.runMode ? ` in ${b.storm.runMode}` : '';
    const cause = b.storm.cause === 'launch' ? 'could not start' : 'was blocked by its environment';
    return `${label} ${cause} ${b.storm.count} times in ${STORM_WINDOW_MS / 60_000} minutes on ${machineName}${mode}: ${b.storm.summary} Orgus paused Autopilot starts of ${label} there until a self-test passes${mode}. ${BREAKER_RECOMMEND['failure-storm']}`;
  }
  const did = [
    b.movedTo ? `moved its work to ${agentKindLabel(b.movedTo)}` : 'moved its work to another agent',
    `starts no new ${label} work on ${machineName} until a self-test passes`,
    ...(b.remedy ? [b.remedy.did] : []),
  ];
  const test = b.lastTest && !b.lastTest.passed && b.lastTest.failedStep ? ` The last self-test failed at: ${SELF_TEST_STEP_LABEL[b.lastTest.failedStep].toLowerCase()}.` : '';
  const evidence = b.fault.excerpt.split('\n')[0] ?? '';
  return `${evidence}${b.fault.count > 1 ? ` (${b.fault.count} times)` : ''}. Orgus ${did.join(', ')}.${test} ${BREAKER_RECOMMEND[b.fault.signature]}`;
}
